import { type ReactNode, useState, useCallback, useMemo, useRef, useEffect } from 'react'
import type { TableColumnPreferences } from '@alpha-edge/shared'
import { colors, tableHeader, tableCell } from './styles'

export interface Column<T> {
  readonly key: string
  readonly header: string
  readonly render: (row: T) => ReactNode
  readonly width?: string
  readonly sortable?: boolean
  readonly filterable?: boolean
  readonly sortValue?: (row: T) => string | number | null
  readonly hideable?: boolean
  readonly defaultVisible?: boolean
}

export type SortDirection = 'asc' | 'desc'

export interface SortState {
  readonly column: string
  readonly direction: SortDirection
}

interface DataTableProps<T> {
  readonly columns: readonly Column<T>[]
  readonly rows: readonly T[]
  readonly keyField: keyof T & string
  readonly onRowClick?: (row: T) => void
  readonly emptyMessage?: string
  readonly selectable?: boolean
  readonly selectedKeys?: ReadonlySet<string>
  readonly onSelectionChange?: (keys: ReadonlySet<string>) => void
  readonly sort?: SortState
  readonly onSortChange?: (sort: SortState) => void
  readonly columnFilters?: Readonly<Record<string, string>>
  readonly onColumnFilterChange?: (key: string, value: string) => void
  readonly tableId?: string
  readonly columnPrefs?: TableColumnPreferences
  readonly onColumnPrefsChange?: (prefs: TableColumnPreferences) => void
}

function SortArrow({ direction, active }: { readonly direction: SortDirection; readonly active: boolean }) {
  if (!active) return <span style={{ color: '#ccc', marginLeft: '4px', fontSize: '0.7rem' }}>{'↕'}</span>
  return (
    <span style={{ marginLeft: '4px', fontSize: '0.7rem', color: colors.accent }}>
      {direction === 'asc' ? '↑' : '↓'}
    </span>
  )
}

function ColumnFilterPopover({
  value,
  onChange,
  onClose,
}: {
  readonly value: string
  readonly onChange: (v: string) => void
  readonly onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [onClose])

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        zIndex: 100,
        background: '#fff',
        border: `1px solid ${colors.border}`,
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        padding: '6px',
        marginTop: '2px',
        minWidth: '160px',
      }}
    >
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Filter..."
          autoFocus
          style={{
            width: '100%',
            padding: '4px 24px 4px 8px',
            border: `1px solid ${colors.borderInput}`,
            borderRadius: '3px',
            fontSize: '0.8125rem',
            boxSizing: 'border-box',
          }}
        />
        {value && (
          <button
            onClick={() => onChange('')}
            style={{
              position: 'absolute',
              right: '4px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              color: '#999',
              cursor: 'pointer',
              fontSize: '0.875rem',
              padding: '0 2px',
              lineHeight: 1,
            }}
            title="Clear filter"
          >
            {'✕'}
          </button>
        )}
      </div>
    </div>
  )
}

function ColumnSettingsPopover<T>({
  columns,
  visibleKeys,
  orderedKeys,
  onToggle,
  onReorder,
  onReset,
  onClose,
}: {
  readonly columns: readonly Column<T>[]
  readonly visibleKeys: ReadonlySet<string>
  readonly orderedKeys: readonly string[]
  readonly onToggle: (key: string) => void
  readonly onReorder: (from: number, to: number) => void
  readonly onReset: () => void
  readonly onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const dragItem = useRef<number | null>(null)
  const dragOver = useRef<number | null>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [onClose])

  const columnMap = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns])

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        zIndex: 100,
        background: '#fff',
        border: `1px solid ${colors.border}`,
        borderRadius: '6px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        padding: '8px 0',
        marginBottom: '4px',
        minWidth: '200px',
        maxHeight: '400px',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          padding: '4px 12px 8px',
          fontSize: '0.75rem',
          color: colors.textMuted,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>Columns</span>
        <button
          onClick={onReset}
          style={{
            background: 'none',
            border: 'none',
            color: colors.accent,
            cursor: 'pointer',
            fontSize: '0.75rem',
            padding: 0,
          }}
        >
          Reset
        </button>
      </div>
      {orderedKeys.map((key, idx) => {
        const col = columnMap.get(key)
        if (!col) return null
        const canHide = col.hideable !== false
        return (
          <div
            key={key}
            draggable
            onDragStart={() => {
              dragItem.current = idx
            }}
            onDragEnter={() => {
              dragOver.current = idx
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragEnd={() => {
              if (dragItem.current !== null && dragOver.current !== null && dragItem.current !== dragOver.current) {
                onReorder(dragItem.current, dragOver.current)
              }
              dragItem.current = null
              dragOver.current = null
            }}
            style={{
              padding: '6px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'grab',
              fontSize: '0.8125rem',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.bgMuted
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = ''
            }}
          >
            <span style={{ color: '#bbb', fontSize: '0.75rem', cursor: 'grab', userSelect: 'none' }}>{'☰'}</span>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', flex: 1 }}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={visibleKeys.has(key)}
                disabled={!canHide}
                onChange={() => onToggle(key)}
                style={{ cursor: canHide ? 'pointer' : 'default' }}
              />
              {col.header}
            </label>
          </div>
        )
      })}
    </div>
  )
}

function extractTextContent(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(extractTextContent).join('')
  if (typeof node === 'object' && 'props' in node) {
    const props = node.props as Record<string, unknown>
    if (props.status) return String(props.status)
    if (props.label) return String(props.label)
    if (props.children) return extractTextContent(props.children as ReactNode)
  }
  return ''
}

export function DataTable<T>({
  columns,
  rows,
  keyField,
  onRowClick,
  emptyMessage,
  selectable,
  selectedKeys,
  onSelectionChange,
  sort: controlledSort,
  onSortChange,
  columnFilters: controlledFilters,
  onColumnFilterChange,
  tableId,
  columnPrefs,
  onColumnPrefsChange,
}: DataTableProps<T>) {
  const [filterOpen, setFilterOpen] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)

  const [internalSort, setInternalSort] = useState<SortState | undefined>(undefined)
  const [internalFilters, setInternalFilters] = useState<Record<string, string>>({})

  const isControlledSort = onSortChange !== undefined
  const isControlledFilter = onColumnFilterChange !== undefined
  const hasColumnSettings = tableId !== undefined && onColumnPrefsChange !== undefined

  const activeSort = isControlledSort ? controlledSort : internalSort
  const activeFilters = useMemo(
    () => (isControlledFilter ? (controlledFilters ?? {}) : internalFilters),
    [isControlledFilter, controlledFilters, internalFilters],
  )

  const defaultColumnKeys = useMemo(() => columns.map((c) => c.key), [columns])

  const defaultVisibleKeys = useMemo(
    () => columns.filter((c) => c.defaultVisible !== false).map((c) => c.key),
    [columns],
  )

  const prefOrder = columnPrefs?.order
  const prefVisible = columnPrefs?.visible

  const orderedKeys = useMemo(() => {
    if (!prefOrder) return defaultColumnKeys
    const allKeys = new Set(defaultColumnKeys)
    const ordered = prefOrder.filter((k) => allKeys.has(k))
    const missing = defaultColumnKeys.filter((k) => !prefOrder.includes(k))
    return [...ordered, ...missing]
  }, [prefOrder, defaultColumnKeys])

  const visibleKeys = useMemo(() => {
    if (!prefVisible) return new Set(defaultVisibleKeys)
    return new Set(prefVisible.filter((k) => defaultColumnKeys.includes(k)))
  }, [prefVisible, defaultVisibleKeys, defaultColumnKeys])

  const visibleColumns = useMemo(() => {
    const columnMap = new Map(columns.map((c) => [c.key, c]))
    return orderedKeys.filter((k) => visibleKeys.has(k)).map((k) => columnMap.get(k)!)
  }, [columns, orderedKeys, visibleKeys])

  const handleToggleColumn = useCallback(
    (key: string) => {
      if (!onColumnPrefsChange) return
      const col = columns.find((c) => c.key === key)
      if (col && col.hideable === false) return
      const next = new Set(visibleKeys)
      if (next.has(key)) {
        if (next.size <= 1) return
        next.delete(key)
      } else {
        next.add(key)
      }
      onColumnPrefsChange({ visible: [...next], order: [...orderedKeys] })
    },
    [columns, visibleKeys, orderedKeys, onColumnPrefsChange],
  )

  const handleReorder = useCallback(
    (from: number, to: number) => {
      if (!onColumnPrefsChange) return
      const next = [...orderedKeys]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      onColumnPrefsChange({ visible: [...visibleKeys], order: next })
    },
    [orderedKeys, visibleKeys, onColumnPrefsChange],
  )

  const handleResetColumns = useCallback(() => {
    if (!onColumnPrefsChange) return
    onColumnPrefsChange({ visible: undefined, order: undefined })
  }, [onColumnPrefsChange])

  const handleHeaderClick = useCallback(
    (col: Column<T>) => {
      if (!col.sortable) return
      const direction: SortDirection = activeSort?.column === col.key && activeSort.direction === 'asc' ? 'desc' : 'asc'
      const next = { column: col.key, direction }
      if (isControlledSort && onSortChange) {
        onSortChange(next)
      } else {
        setInternalSort(next)
      }
    },
    [activeSort, isControlledSort, onSortChange],
  )

  const handleFilterChange = useCallback(
    (key: string, value: string) => {
      if (isControlledFilter && onColumnFilterChange) {
        onColumnFilterChange(key, value)
      } else {
        setInternalFilters((prev) => ({ ...prev, [key]: value }))
      }
    },
    [isControlledFilter, onColumnFilterChange],
  )

  const handleFilterClick = useCallback(
    (e: React.MouseEvent, colKey: string) => {
      e.stopPropagation()
      setFilterOpen(filterOpen === colKey ? null : colKey)
    },
    [filterOpen],
  )

  const toggleSelectAll = useCallback(() => {
    if (!onSelectionChange) return
    const allKeys = new Set(rows.map((r) => String(r[keyField])))
    const allSelected = selectedKeys ? allKeys.size === selectedKeys.size : false
    onSelectionChange(allSelected ? new Set() : allKeys)
  }, [rows, keyField, selectedKeys, onSelectionChange])

  const toggleSelect = useCallback(
    (key: string) => {
      if (!onSelectionChange || !selectedKeys) return
      const next = new Set(selectedKeys)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      onSelectionChange(next)
    },
    [selectedKeys, onSelectionChange],
  )

  const columnMap = useMemo(() => new Map(visibleColumns.map((c) => [c.key, c])), [visibleColumns])

  const processedRows = useMemo(() => {
    let result = [...rows]

    const activeFilterEntries = Object.entries(activeFilters).filter(([, v]) => v.length > 0)
    if (activeFilterEntries.length > 0) {
      result = result.filter((row) =>
        activeFilterEntries.every(([key, filterValue]) => {
          const col = columnMap.get(key)
          if (!col) return true
          const cellText = extractTextContent(col.render(row)).toLowerCase()
          return cellText.includes(filterValue.toLowerCase())
        }),
      )
    }

    if (activeSort) {
      const sortCol = columnMap.get(activeSort.column)
      if (sortCol) {
        const dir = activeSort.direction === 'asc' ? 1 : -1
        result.sort((a, b) => {
          let aVal: string | number | null
          let bVal: string | number | null
          if (sortCol.sortValue) {
            aVal = sortCol.sortValue(a)
            bVal = sortCol.sortValue(b)
          } else {
            aVal = extractTextContent(sortCol.render(a))
            bVal = extractTextContent(sortCol.render(b))
          }
          if (aVal == null && bVal == null) return 0
          if (aVal == null) return dir
          if (bVal == null) return -dir
          if (typeof aVal === 'number' && typeof bVal === 'number') return (aVal - bVal) * dir
          return String(aVal).localeCompare(String(bVal)) * dir
        })
      }
    }

    return result
  }, [rows, activeSort, activeFilters, columnMap])

  if (processedRows.length === 0 && rows.length === 0) {
    return (
      <div
        style={{
          padding: '2rem',
          textAlign: 'center',
          color: colors.textMuted,
          background: colors.bgCard,
          borderRadius: '4px',
          border: `1px solid ${colors.border}`,
        }}
      >
        {emptyMessage ?? 'No records found.'}
      </div>
    )
  }

  const allSelected = selectedKeys ? selectedKeys.size === processedRows.length && processedRows.length > 0 : false

  const hasFilterable = visibleColumns.some((c) => c.filterable)

  return (
    <div
      style={{
        background: colors.bgCard,
        borderRadius: '4px',
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
      }}
    >
      {hasFilterable && Object.values(activeFilters).some((v) => v.length > 0) && (
        <div
          style={{
            padding: '0.4rem 0.75rem',
            background: '#f0f4ff',
            borderBottom: `1px solid ${colors.border}`,
            fontSize: '0.8125rem',
            color: colors.textMuted,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <span>Filtered</span>
          <button
            onClick={() =>
              isControlledFilter
                ? Object.keys(activeFilters).forEach((k) => onColumnFilterChange?.(k, ''))
                : setInternalFilters({})
            }
            style={{
              background: 'none',
              border: 'none',
              color: colors.accent,
              cursor: 'pointer',
              fontSize: '0.8125rem',
              padding: 0,
            }}
          >
            Clear all
          </button>
          {processedRows.length !== rows.length && (
            <span>
              ({processedRows.length} of {rows.length})
            </span>
          )}
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {selectable && (
              <th style={{ ...tableHeader, width: '36px', cursor: 'default' }}>
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} style={{ cursor: 'pointer' }} />
              </th>
            )}
            {visibleColumns.map((col, colIdx) => (
              <th
                key={col.key}
                style={{
                  ...tableHeader,
                  width: col.width,
                  position: 'relative',
                  cursor: col.sortable || col.filterable ? 'pointer' : 'default',
                }}
                onClick={() => handleHeaderClick(col)}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  {colIdx === 0 && hasColumnSettings && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation()
                        setSettingsOpen(!settingsOpen)
                      }}
                      style={{
                        marginRight: '6px',
                        cursor: 'pointer',
                        fontSize: '0.8125rem',
                        color: settingsOpen ? colors.accent : '#bbb',
                        position: 'relative',
                        lineHeight: 1,
                      }}
                      title="Column settings"
                    >
                      {'⚙'}
                      {settingsOpen && (
                        <ColumnSettingsPopover
                          columns={columns}
                          visibleKeys={visibleKeys}
                          orderedKeys={orderedKeys}
                          onToggle={handleToggleColumn}
                          onReorder={handleReorder}
                          onReset={handleResetColumns}
                          onClose={() => setSettingsOpen(false)}
                        />
                      )}
                    </span>
                  )}
                  {col.header}
                  {col.sortable && (
                    <SortArrow
                      direction={activeSort?.column === col.key ? activeSort.direction : 'asc'}
                      active={activeSort?.column === col.key}
                    />
                  )}
                  {col.filterable && (
                    <span
                      onClick={(e) => handleFilterClick(e, col.key)}
                      style={{
                        marginLeft: '4px',
                        cursor: 'pointer',
                        fontSize: '0.7rem',
                        color: activeFilters[col.key] ? colors.accent : '#ccc',
                        position: 'relative',
                      }}
                      title={`Filter ${col.header}`}
                    >
                      {'▼'}
                    </span>
                  )}
                </span>
                {filterOpen === col.key && (
                  <ColumnFilterPopover
                    value={activeFilters[col.key] ?? ''}
                    onChange={(v) => handleFilterChange(col.key, v)}
                    onClose={() => setFilterOpen(null)}
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {processedRows.map((row) => {
            const rowKey = String(row[keyField])
            const isSelected = selectedKeys?.has(rowKey) ?? false
            return (
              <tr
                key={rowKey}
                onClick={() => onRowClick?.(row)}
                style={{
                  cursor: onRowClick ? 'pointer' : undefined,
                  background: isSelected ? colors.tableRowSelected : undefined,
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.background = colors.tableRowHover
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isSelected ? colors.tableRowSelected : ''
                }}
              >
                {selectable && (
                  <td style={{ ...tableCell, width: '36px' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(rowKey)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                )}
                {visibleColumns.map((col) => (
                  <td key={col.key} style={tableCell}>
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            )
          })}
          {processedRows.length === 0 && rows.length > 0 && (
            <tr>
              <td
                colSpan={visibleColumns.length + (selectable ? 1 : 0)}
                style={{ ...tableCell, textAlign: 'center', color: colors.textMuted }}
              >
                No rows match the current filter.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
